import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { client } from "@/lib/api";

const PRIORITIES = [
  { key: "equipment_down", label: "Equipment down", tint: "#B91C1C" },
  { key: "standard", label: "Standard", tint: "#B45309" },
  { key: "low", label: "Low", tint: "#94A3B8" },
];

export default function SlaSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setSettings((await client.get("/settings/sla")).data); }
    catch { toast.error("Could not load SLA settings"); }
  };
  useEffect(() => { load(); }, []);

  const setTarget = (group, key, value) =>
    setSettings(s => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sla_targets: settings.sla_targets,
        peak_sla_targets: settings.peak_sla_targets,
        peak_enabled: settings.peak_enabled,
        peak_start: settings.peak_start,
        peak_end: settings.peak_end,
      };
      const r = await client.patch("/settings/sla", payload);
      setSettings(r.data);
      toast.success("SLA & season settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="mono text-[12px]" style={{ color: "#5F6B7A" }} data-testid="sla-settings-loading">Loading…</div>;

  return (
    <div data-testid="sla-settings-page">
      <Toaster richColors position="top-right" />
      <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Settings</div>
      <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>SLA &amp; service seasons</h1>
      <p className="mt-1 text-[13.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
        Set how fast each priority must be met. Peak-season targets apply automatically on new tickets during your busy season.
      </p>

      {settings.peak_active_now && (
        <div className="mt-4 bf-chip mono"
          style={{ background: "#F2EEFE", color: "#5B21B6", padding: "7px 12px", fontSize: "12.5px" }}
          data-testid="peak-active-badge">
          <span className="bf-dot" style={{ background: "#5B21B6" }} />
          Peak season is active now — peak targets are in effect.
        </div>
      )}

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TargetCard title="Standard targets" subtitle="Used off-season"
          group="sla_targets" settings={settings} setTarget={setTarget} testid="standard-targets" />
        <TargetCard title="Peak-season targets" subtitle="Used during busy season"
          group="peak_sla_targets" settings={settings} setTarget={setTarget} testid="peak-targets" />
      </div>

      <div className="mt-5 bf-card" style={{ padding: "20px" }} data-testid="season-window-card">
        <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>Peak season window</div>
        <div className="mt-3 flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2 text-[13.5px] cursor-pointer" data-testid="peak-enabled-toggle">
            <input type="checkbox" checked={!!settings.peak_enabled} style={{ accentColor: "#2563EB" }}
              onChange={e => setSettings(s => ({ ...s, peak_enabled: e.target.checked }))} />
            Apply peak-season targets automatically
          </label>
          <div>
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-1" style={{ color: "#5B6472" }}>Season start (MM-DD)</div>
            <Input className="mono w-32 rounded-[10px]" value={settings.peak_start || ""} placeholder="12-01"
              onChange={e => setSettings(s => ({ ...s, peak_start: e.target.value }))} data-testid="peak-start-input" />
          </div>
          <div>
            <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-1" style={{ color: "#5B6472" }}>Season end (MM-DD)</div>
            <Input className="mono w-32 rounded-[10px]" value={settings.peak_end || ""} placeholder="04-30"
              onChange={e => setSettings(s => ({ ...s, peak_end: e.target.value }))} data-testid="peak-end-input" />
          </div>
        </div>
        <p className="mt-3 text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
          Barbados tourist season spans year-end (e.g. 12-01 → 04-30). SLA targets on new tickets are chosen by the ticket's creation date.
        </p>
      </div>

      <Button className="mt-6 bf-btn-primary px-4" onClick={save} disabled={saving} data-testid="save-sla-settings-button">
        <Save size={15} className="mr-2" />{saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}

function TargetCard({ title, subtitle, group, settings, setTarget, testid }) {
  return (
    <div className="bf-card" style={{ padding: "20px" }} data-testid={testid}>
      <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>{title}</div>
      <div className="text-[13px]" style={{ color: "#5B6472" }}>{subtitle}</div>
      <div className="mt-4 space-y-2.5">
        {PRIORITIES.map(p => (
          <div key={p.key} className="flex items-center gap-3 rounded-[12px] px-3 py-2" style={{ background: "#F1F3F6" }}>
            <span className="bf-dot" style={{ background: p.tint }} />
            <div className="flex-1 text-[13.5px]">{p.label}</div>
            <Input type="number" min="1" className="mono w-24 text-right rounded-[10px] bg-white"
              value={settings[group]?.[p.key] ?? ""}
              onChange={e => setTarget(group, p.key, e.target.value)}
              data-testid={`${testid}-${p.key}-input`} />
            <span className="text-[13px] w-10" style={{ color: "#5B6472" }}>hours</span>
          </div>
        ))}
      </div>
    </div>
  );
}
